/* ==========================================================================
   Granatelli — Header / Announcement / Hero behaviors.
   - <gm-header-effects>: transparent → solid on scroll, header height var.
   - Announcement bar dismiss with localStorage.
   - <gm-hero-slideshow>: fade slider with autoplay, dots, play/pause.
   ========================================================================== */

/* ---------- Header transparent / scrolled ------------------------------- */

class GmHeaderEffects extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._ticking = false;
  }

  connectedCallback() {
    this.wrapper = this.querySelector('.gm-header-wrapper');
    if (!this.wrapper) return;
    this.transparent = this.dataset.transparent === 'true';
    this.scrollTrigger = parseInt(this.dataset.scrollTrigger, 10);
    if (Number.isNaN(this.scrollTrigger)) this.scrollTrigger = 80;
    this._publishHeight();
    this._cacheHero();
    // Only enter the transparent overlay state when there's an actual hero /
    // transparent-zone on the page. Inner pages (product, collection, etc.)
    // have no hero, so the header must render solid from the start —
    // otherwise the white-text nav vanishes onto the white body background.
    if (this.transparent && this.hero) this.wrapper.classList.add('gm-is-transparent');
    this._evaluate();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  _cacheHero() {
    // Element that defines the "transparent zone": typically the hero/slideshow.
    // Falls back gracefully if no hero is on the page.
    this.hero = document.querySelector('.gm-hero, .gm-hero-section, [data-gm-transparent-zone]');
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
  }

  _onScroll() {
    // Force-hide any open mega menu while the user is actively scrolling.
    // CSS rule `.gm-header-wrapper.gm-header--scrolling .gm-mega-menu`
    // forces opacity:0/visibility:hidden. Timer is reset on every scroll
    // event and the class is removed 150ms after the last one, so the
    // mega menu reappears (via :hover) once the user stops scrolling.
    if (this.wrapper && !this.wrapper.classList.contains('gm-header--scrolling')) {
      this.wrapper.classList.add('gm-header--scrolling');
    }
    if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer);
    this._scrollEndTimer = setTimeout(() => {
      if (this.wrapper) this.wrapper.classList.remove('gm-header--scrolling');
    }, 150);

    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => { this._evaluate(); this._ticking = false; });
  }
  _onResize() { this._publishHeight(); this._cacheHero(); this._evaluate(); }

  _publishHeight() {
    if (!this.wrapper) return;
    document.documentElement.style.setProperty('--gm-header-rendered-height', `${this.wrapper.offsetHeight}px`);
  }

  _evaluate() {
    let past;
    if (this.transparent && this.hero) {
      // Header stays transparent for as long as the hero overlaps the bottom
      // edge of the header. As soon as the hero is fully scrolled past, switch
      // to the solid (scrolled) state.
      const heroBottom = this.hero.getBoundingClientRect().bottom;
      const headerH = this.wrapper.offsetHeight;
      const vbarH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--gm-vbar-height')) || 0;
      past = heroBottom <= (headerH + vbarH);
    } else if (this.transparent) {
      // Transparent setting is enabled, but this page has no hero/transparent
      // zone — force the solid state so the nav stays visible.
      past = true;
    } else {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      past = y > this.scrollTrigger;
    }
    if (past) {
      this.wrapper.classList.add('gm-scrolled');
      if (this.transparent) this.wrapper.classList.remove('gm-is-transparent');
    } else {
      this.wrapper.classList.remove('gm-scrolled');
      if (this.transparent) this.wrapper.classList.add('gm-is-transparent');
    }
  }
}

if (!customElements.get('gm-header-effects')) customElements.define('gm-header-effects', GmHeaderEffects);

/* ---------- Mobile menu drawer — portal to <body> ----------------------- */
/* The drawer is wrapped in Dawn's .menu-drawer-container which sits inside
   the header's stacking context + a transform/filter parent. That traps
   position:fixed inside the header, so the drawer can't cover the bars
   above it. Move the drawer + backdrop to <body> on load, then sync the
   .is-open class with the <details> toggle state via JS. */
(function () {
  function portalDrawer() {
    document.querySelectorAll('details.menu-drawer-container').forEach(function (details) {
      var drawer = details.querySelector('.gm-drawer');
      var backdrop = details.querySelector('.gm-drawer-backdrop');
      if (!drawer || drawer.dataset.gmPortaled === 'true') return;

      drawer.dataset.gmPortaled = 'true';
      // Move to <body> so position:fixed is anchored to the viewport.
      document.body.appendChild(drawer);
      if (backdrop) {
        backdrop.dataset.gmPortaled = 'true';
        document.body.appendChild(backdrop);
      }

      // Body-scroll-lock classes Dawn (or other components) add when the
      // drawer opens. data-breakpoint on <header-drawer> can be "desktop"
      // or "tablet" — strip every variant on close so nothing lingers.
      var OVERFLOW_CLASSES = [
        'overflow-hidden',
        'overflow-hidden-mobile',
        'overflow-hidden-tablet',
        'overflow-hidden-desktop',
      ];

      function setOpenState() {
        if (details.open) {
          drawer.classList.add('is-open');
          if (backdrop) backdrop.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        } else {
          drawer.classList.remove('is-open');
          if (backdrop) backdrop.classList.remove('is-open');
          document.body.style.overflow = '';
          // Dawn's MenuDrawer.openMenuDrawer adds `overflow-hidden-${breakpoint}`
          // but its closeMenuDrawer only runs on its own close paths. When we
          // close the drawer ourselves (close button / backdrop / Esc / programmatic
          // removeAttribute('open')), Dawn's removal never runs — so we clear
          // every variant here.
          document.body.classList.remove.apply(document.body.classList, OVERFLOW_CLASSES);
        }
      }

      details.addEventListener('toggle', setOpenState);
      // Already-open state (Shopify theme editor re-renders)
      if (details.open) setOpenState();

      // Close-button + backdrop handlers — attached HERE because after
      // portaling, the drawer is no longer a descendant of <details>, so
      // initDrawerControls's drawer.closest('details') returns null and
      // its handlers are never bound. We have direct refs to both nodes
      // here, so wire them up directly.
      drawer.querySelectorAll('[data-gm-drawer-close]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
          e.preventDefault();
          details.removeAttribute('open');
        });
      });

      // Backdrop click closes the drawer
      if (backdrop) {
        backdrop.addEventListener('click', function () {
          details.removeAttribute('open');
        });
      }

      // "Find Parts For My Vehicle" CTA — if the user has typed something in
      // the drawer search field, send them to the search results page with
      // that query. We build the URL from the drawer's own search form (so
      // type/prefix carry over) and navigate directly, rather than submitting
      // the form: predictive-search.js intercepts the form's submit event when
      // a suggestion is keyboard-selected, which would block the redirect.
      // With an empty field it falls through to the link's default href (the
      // configured vehicle-finder page).
      var findParts = drawer.querySelector('.gm-drawer__find-parts');
      var searchForm = drawer.querySelector('.gm-drawer__search');
      var searchInput = drawer.querySelector('.gm-drawer__search-input');
      if (findParts && searchForm && searchInput) {
        findParts.addEventListener('click', function (e) {
          var term = searchInput.value.trim();
          if (term === '') return; // keep default href
          e.preventDefault();
          var action = searchForm.getAttribute('action') || '/search';
          var params = new URLSearchParams();
          new FormData(searchForm).forEach(function (value, key) {
            params.set(key, value);
          });
          params.set('q', term);
          window.location.href = action + '?' + params.toString();
        });
      }

      // Esc closes
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && details.open) {
          details.removeAttribute('open');
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', portalDrawer);
  } else {
    portalDrawer();
  }
  document.addEventListener('shopify:section:load', portalDrawer);
})();

/* ---------- Vehicle status bar height publisher ------------------------- */
/* Measures the rendered vehicle bar height and exposes it as
   --gm-vbar-height on :root so the header below can stick at that offset. */

(function publishVehicleBarHeight() {
  const ROOT = document.documentElement;

  const update = () => {
    const bar = document.querySelector('.gm-vehicle-bar-section');
    const h = bar && bar.offsetHeight ? bar.offsetHeight : 0;
    ROOT.style.setProperty('--gm-vbar-height', `${h}px`);
  };

  const init = () => {
    update();
    window.addEventListener('resize', update, { passive: true });
    if (window.ResizeObserver) {
      const bar = document.querySelector('.gm-vehicle-bar-section');
      if (bar) new ResizeObserver(update).observe(bar);
    }
    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:load', update);
      document.addEventListener('shopify:section:unload', update);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ---------- Announcement bar dismiss ------------------------------------ */
/* Per-pageview dismissal: clicking close hides the bar for the rest of the
   session, but a refresh restores it. Intentionally NOT persisted to
   localStorage / sessionStorage — the merchant wants the message re-shown
   each time the page loads. */

(function initAnnouncementDismiss() {
  const onReady = () => {
    document.querySelectorAll('.gm-utility-bar[data-dismissible="true"]').forEach((bar) => {
      const btn = bar.querySelector('[data-gm-dismiss]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        bar.classList.add('is-dismissed');
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();

/* ---------- Mobile drawer — close button + search-focus button --------- */
/* The drawer is a native <details>; "open" is removed to close it. The
   search-focus button just moves focus into the search input (so users who
   tap the magnifier icon land on the field without leaving the drawer). */

(function initDrawerControls() {
  // All body-scroll-lock classes that Dawn (or other components) may add
  // when the drawer opens. data-breakpoint on <header-drawer> can be
  // "desktop" or "tablet" — both need to be cleared on close.
  const OVERFLOW_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
  ];

  const onReady = () => {
    document.querySelectorAll('.gm-drawer').forEach((drawer) => {
      const details = drawer.closest('details.menu-drawer-container');
      if (!details) return;

      // Single source of truth: whenever the <details> toggles closed, strip
      // every overflow-hidden* class. Catches every close path (close button,
      // summary re-click, focus-out, Escape, programmatic removeAttribute).
      details.addEventListener('toggle', () => {
        if (!details.hasAttribute('open')) {
          document.body.classList.remove(...OVERFLOW_CLASSES);
        }
      });

      // Close triggers — the in-drawer close button AND the backdrop.
      details.querySelectorAll('[data-gm-drawer-close]').forEach((trigger) => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          details.removeAttribute('open');
          // toggle listener above strips body classes.
        });
      });

      // Esc key closes the drawer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && details.hasAttribute('open')) {
          details.removeAttribute('open');
        }
      });

    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();
})();

/* ---------- Hero slideshow --------------------------------------------- */

class GmHeroSlideshow extends HTMLElement {
  constructor() {
    super();
    this._onVisibility = this._onVisibility.bind(this);
    this._tick = this._tick.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-gm-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-gm-dot]'));
    this.playPauseBtn = this.querySelector('[data-gm-playpause]');
    this.iconPause = this.querySelector('.gm-hero__icon-pause');
    this.iconPlay = this.querySelector('.gm-hero__icon-play');
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = (parseInt(this.dataset.speed, 10) || 6) * 1000;
    this.index = 0;
    this.timer = null;
    this.paused = !this.autoplay;
    if (this.slides.length === 0) return;
    this._show(0);
    this._syncIcons();
    this.addEventListener('click', (e) => {
      const dot = e.target.closest('[data-gm-dot]');
      if (dot && this.contains(dot)) {
        e.preventDefault();
        const idx = parseInt(dot.dataset.index, 10);
        this._show(idx);
        this._restart();
        return;
      }
      const pp = e.target.closest('[data-gm-playpause]');
      if (pp && this.contains(pp)) {
        e.preventDefault();
        this._togglePlayPause();
      }
    });
    document.addEventListener('visibilitychange', this._onVisibility);
    if (this.autoplay && this.slides.length > 1) this._start();
  }

  _syncIcons() {
    if (this.iconPause) this.iconPause.style.display = this.paused ? 'none' : 'inline-block';
    if (this.iconPlay) this.iconPlay.style.display = this.paused ? 'inline-block' : 'none';
  }

  disconnectedCallback() {
    this._stop();
    document.removeEventListener('visibilitychange', this._onVisibility);
  }

  _onVisibility() {
    if (document.hidden) this._stop(true);
    else if (!this.paused) this._start();
  }

  _show(idx) {
    if (idx < 0) idx = this.slides.length - 1;
    if (idx >= this.slides.length) idx = 0;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === idx));
    this.dots.forEach((d, i) => {
      d.classList.toggle('is-active', i === idx);
      d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
    this.index = idx;
  }

  _next() { this._show(this.index + 1); }

  _start() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
    if (this.slides.length < 2) return;
    this.timer = setInterval(this._tick, this.speed);
  }

  _stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  _restart() {
    if (!this.paused) this._start();
  }

  _tick() { this._next(); }

  _togglePlayPause() {
    this.paused = !this.paused;
    if (this.playPauseBtn) {
      this.playPauseBtn.classList.toggle('is-paused', this.paused);
      this.playPauseBtn.setAttribute('aria-label', this.paused ? 'Play autoplay' : 'Pause autoplay');
    }
    this._syncIcons();
    if (this.paused) this._stop(true);
    else this._start();
  }
}

if (!customElements.get('gm-hero-slideshow')) customElements.define('gm-hero-slideshow', GmHeroSlideshow);
